import { DefaultLayout } from "@bg/shared/lib/components";
import {
  Button,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import "react-phone-input-2/lib/material.css";
import React, { useLayoutEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import Footer from "@bg/shared/lib/components/Footer";
import { HipaaNoticeDialog } from "@bg/orders/src/views/Dialogs";
import axios from "axios";
import { useSnackbar } from "notistack";
import { postGCAppointment } from "@bg/shared/lib/dal/orders";
import parsePhoneNumberFromString from "libphonenumber-js";

type Errors = {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  email?: string;
  phoneNumber?: string;
  preferredContact?: string;
};

const useStyles = makeStyles((theme) => ({
  container: {
    margin: "2rem auto",
    padding: "2rem",
    fontFamily: "Roboto, sans-serif",
  },
  title: {
    fontSize: "2rem",
    fontWeight: 400,
    margin: "2rem",
    color: "#004A7C",
    textAlign: "center",
  },
  textField: {
    width: "100%",
  },
  inputWhite: {
    backgroundColor: "#FFFFFF",
  },
  formControl: {
    width: "100%",
  },
  buttonBox: {
    marginTop: "2rem",
    display: "flex",
    justifyContent: "flex-end",
  },
  consentText: {
    fontSize: "0.875rem",
    margin: "1rem 0",
    color: "#555",
  },
  infoBox: {
    backgroundColor: "#f8f8fb",
    padding: "2rem",
    margin: "2rem auto",
    borderRadius: "8px",
    maxWidth: "900px",
    fontFamily: "Roboto, sans-serif",
  },
  infoText: {
    color: "#001C54",
    fontSize: "1rem",
    marginBottom: "1rem",
    "& b": {
      fontWeight: 600,
    },
  },
  submitButton: {
    backgroundColor: "#28A8E0",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#1f94c4",
    },
  },
  outerContainer: {
    minHeight: "calc(100vh - 6.66rem)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    background: "#fff",
    paddingTop: "4em",
    paddingBottom: "2em",
    boxSizing: "border-box",
    width: "100%",
    overflowX: "hidden",
  },
  innerContainer: {
    maxWidth: 1000,
    margin: "0 auto",
    padding: "0 2em",
    boxSizing: "border-box",
  },
  headerBar: {
    maxWidth: "100vw",
    margin: "0 auto",
    width: "100%",
    height: "3.66rem",
    background: "#160E44",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: "0 2em",
    zIndex: 1000,
    boxSizing: "border-box",
  },
  headerLogo: {
    height: "3.66rem",
    margin: 0,
  },
}));

const AppointmentForm = () => {
  const initialFormData = {
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    labNumber: "",
    guardianFirstName: "",
    guardianLastName: "",
    email: "",
    phoneNumber: "",
    preferredLanguage: "English",
    preferredContact: "",
    insurancePolicyName: "",
    insuranceId: "",
    insuranceGroup: "",
  };
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [hipaaAcknowledged, setHipaaAcknowledged] = useState(false);
  const [openHipaaModal, setOpenHipaaModal] = useState(false);

  // Set document title and prevent horizontal scrolling when form is open
  useLayoutEffect(() => {
    document.title = "Appointment-form";
    document.body.style.overflowX = "hidden";
    // Restore default overflow when component unmounts
    return () => {
      document.body.style.overflowX = "";
    };
  }, []);

  // Validate form fields and collect errors
  const validate = () => {
    const newErrors: Errors = {};
    // Check required fields and add error messages as needed
    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.dateOfBirth)
      newErrors.dateOfBirth = "Date of birth is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }

    const phone = formData.phoneNumber.trim();
    let phoneNumber;

    if (!phone) {
      newErrors.phoneNumber = "Phone number is required";
    } else {
      phoneNumber = parsePhoneNumberFromString(
        phone.startsWith("+") ? phone : `+${phone}`,
      );
      if (!phoneNumber || !phoneNumber.isValid()) {
        newErrors.phoneNumber = "Enter a valid Phone Number";
      }
    }
    if (!formData.preferredContact)
      newErrors.preferredContact = "Preferred contact is required";
    setErrors(newErrors);
    // Return true if no errors
    return Object.keys(newErrors).length === 0;
  };

  // Add a function to check if the form is valid (all required fields filled and no errors)
  const isFormValid = () => {
    return (
      formData.firstName.trim() &&
      formData.lastName.trim() &&
      formData.dateOfBirth &&
      formData.email.trim() &&
      formData.phoneNumber.trim() &&
      formData.preferredContact &&
      hipaaAcknowledged
    );
  };

  // Handle changes to form fields
  const handleChange =
    (field: keyof typeof formData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let value = event.target.value;
      // Restrict input to only allow letters, spaces, apostrophes, and hyphens
      if (
        field === "firstName" ||
        field === "lastName" ||
        field === "guardianFirstName" ||
        field === "guardianLastName"
      ) {
        value = value.replace(/[^a-zA-Z\s'-]/g, "");
      }
      setFormData((prev) => ({ ...prev, [field]: value }));
      setErrors((prev: Errors) => ({ ...prev, [field]: undefined }));
    };
  const handleSelectChange =
    (field: keyof typeof formData) =>
    (event: React.ChangeEvent<{ value: unknown }>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value as string,
      }));
      setErrors((prev: Errors) => ({ ...prev, [field]: undefined }));
    };

  // Handle phone number input changes
  const handlePhoneChange = (phone: string) => {
    setFormData((prev) => ({ ...prev, phoneNumber: phone }));
    setErrors((prev: Errors) => ({ ...prev, phoneNumber: undefined }));
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    // Validate form before submitting
    if (validate()) {
      const { phoneNumber, dateOfBirth, ...rest } = formData;
      // Format dateOfBirth to yyyy/MM/dd for API
      let formattedDOB = dateOfBirth;
      if (dateOfBirth && dateOfBirth.includes("-")) {
        const [yyyy, mm, dd] = dateOfBirth.split("-");
        formattedDOB = `${yyyy}-${mm}-${dd}`;
      }
      const payload = {
        ...rest,
        dateOfBirth: formattedDOB,
        phoneNumber: formData.phoneNumber.startsWith("+")
          ? formData.phoneNumber
          : `+${formData.phoneNumber}`,
      };
      setLoading(true);
      try {
        // Submit appointment request to backend API
        await postGCAppointment(payload);
        setLoading(false);
        setFormData(initialFormData);
        setHipaaAcknowledged(false);
        enqueueSnackbar("Appointment request submitted successfully!", {
          variant: "success",
        });
      } catch (err: any) {
        setLoading(false);
        enqueueSnackbar(
          "Unable to submit appointment. Please try again later.",
          { variant: "error" },
        );
      }
    }
  };

  return (
    <>
      <div className={classes.headerBar}>
        <img
          src="/images/logo-reverse.svg"
          alt="Baylor Genetics Logo"
          className={classes.headerLogo}
        />
      </div>
      <div className={classes.outerContainer}>
        <div className={classes.innerContainer}>
          <Typography className={classes.title}>
            Request a Genetic Counseling Appointment
          </Typography>
          <Paper elevation={0} className={classes.infoBox}>
            <Typography className={classes.infoText}>
              <b>
                Baylor Genetics has teamed up with Genome Medical to offer
                access to expert genetic counseling for select test results.
              </b>
            </Typography>
            <Typography className={classes.infoText}>
              If you've received a test result that qualifies for counseling, we
              encourage you to take the next step in understanding your genetic
              information.
            </Typography>
            <Typography className={classes.infoText}>
              Please complete the form below to request a genetic counseling
              appointment. Once submitted, a representative from Genome Medical
              will reach out to you directly to coordinate and schedule your
              session.
            </Typography>
          </Paper>
          <form onSubmit={handleSubmit} noValidate>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <TextField
                  className={classes.textField}
                  label="Patient's First Name"
                  variant="outlined"
                  value={formData.firstName}
                  onChange={handleChange("firstName")}
                  required
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  inputProps={{ maxLength: 225 }}
                  InputProps={{ classes: { input: classes.inputWhite } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  className={classes.textField}
                  label="Patient's Last Name"
                  variant="outlined"
                  value={formData.lastName}
                  onChange={handleChange("lastName")}
                  required
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  inputProps={{ maxLength: 10 }}
                  InputProps={{ classes: { input: classes.inputWhite } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  className={classes.textField}
                  label="Date of Birth"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  value={formData.dateOfBirth}
                  onChange={handleChange("dateOfBirth")}
                  required
                  error={!!errors.dateOfBirth}
                  helperText={errors.dateOfBirth}
                  inputProps={{ max: new Date().toISOString().split("T")[0] }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  className={classes.textField}
                  label="Lab Number from Baylor Genetics Report (if available)"
                  variant="outlined"
                  value={formData.labNumber}
                  onChange={handleChange("labNumber")}
                  inputProps={{ maxLength: 10 }}
                  InputProps={{ classes: { input: classes.inputWhite } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  className={classes.textField}
                  label="Parent/Guardian First Name (if applicable)"
                  variant="outlined"
                  value={formData.guardianFirstName}
                  onChange={handleChange("guardianFirstName")}
                  inputProps={{ maxLength: 40 }}
                  InputProps={{ classes: { input: classes.inputWhite } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  className={classes.textField}
                  label="Parent/Guardian Last Name (if applicable)"
                  variant="outlined"
                  value={formData.guardianLastName}
                  onChange={handleChange("guardianLastName")}
                  inputProps={{ maxLength: 39 }}
                  InputProps={{ classes: { input: classes.inputWhite } }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  className={classes.textField}
                  label="Email"
                  variant="outlined"
                  value={formData.email}
                  onChange={handleChange("email")}
                  required
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{ classes: { input: classes.inputWhite } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <PhoneInput
                  country={"us"}
                  value={formData.phoneNumber}
                  onChange={handlePhoneChange}
                  specialLabel="Phone *"
                  inputProps={{
                    name: "phone",
                    required: true,
                  }}
                  inputStyle={{
                    width: "100%",
                    backgroundColor: "#fff",
                    height: "56px",
                    borderRadius: "4px",
                    fontSize: "1rem",
                    borderColor: errors.phoneNumber ? "#f44336" : undefined,
                  }}
                  containerStyle={{
                    width: "100%",
                  }}
                  buttonStyle={{
                    borderTopLeftRadius: "4px",
                    borderBottomLeftRadius: "4px",
                  }}
                  placeholder="Enter phone number *"
                />
                {errors.phoneNumber && (
                  <div
                    style={{
                      color: "#f44336",
                      fontSize: "0.75rem",
                      marginLeft: "14px",
                      marginTop: "3px",
                    }}
                  >
                    {errors.phoneNumber}
                  </div>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl
                  variant="outlined"
                  fullWidth
                  error={!!errors.preferredContact}
                >
                  <InputLabel id="opt-out-label" required>
                    Would you like to opt out from receiving text messages?
                  </InputLabel>
                  <Select
                    labelId="opt-out-label"
                    id="opt-out"
                    label="Would you like to opt out from receiving text messages?"
                    value={formData.preferredContact}
                    onChange={handleSelectChange("preferredContact")}
                    classes={{ root: classes.inputWhite }}
                  >
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </Select>
                  <FormHelperText>
                    {errors.preferredContact ||
                      "By providing your phone number to Genome Medical, you consent to receive text messages from or on behalf of Genome Medical, including but not limited to transactional messages and service-related messages at the phone number provided. Message and data rates may apply. Message frequency may vary. You can opt out of receiving text messages at any time by replying STOP to any message."}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="preferred-language-label">
                    Preferred language for genetic counseling session
                  </InputLabel>
                  <Select
                    labelId="preferred-language-label"
                    id="preferred-language"
                    label="Preferred language for genetic counseling session"
                    value={formData.preferredLanguage}
                    onChange={handleSelectChange("preferredLanguage")}
                    classes={{ root: classes.inputWhite }}
                  >
                    <MenuItem value="English">English</MenuItem>
                    <MenuItem value="Spanish">Spanish</MenuItem>
                    <MenuItem value="Mandarin">Mandarin</MenuItem>
                    <MenuItem value="Cantonese">Cantonese</MenuItem>
                    <MenuItem value="Arabic">Arabic</MenuItem>
                    <MenuItem value="American Sign Language">
                      American Sign Language
                    </MenuItem>
                    <MenuItem value="Vietnamese">Vietnamese</MenuItem>
                    <MenuItem value="Russian">Russian</MenuItem>
                    <MenuItem value="Haitian Creole">Haitian Creole</MenuItem>
                    <MenuItem value="Korean">Korean</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  className={classes.textField}
                  label="Patient's Insurance Company / Policy Name"
                  variant="outlined"
                  value={formData.insurancePolicyName}
                  onChange={handleChange("insurancePolicyName")}
                  inputProps={{ maxLength: 255 }}
                  InputProps={{ classes: { input: classes.inputWhite } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  className={classes.textField}
                  label="Patient's Insurance ID #"
                  variant="outlined"
                  value={formData.insuranceId}
                  onChange={handleChange("insuranceId")}
                  inputProps={{ maxLength: 50 }}
                  InputProps={{ classes: { input: classes.inputWhite } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  className={classes.textField}
                  label="Patient's Insurance Group #"
                  variant="outlined"
                  value={formData.insuranceGroup}
                  onChange={handleChange("insuranceGroup")}
                  inputProps={{ maxLength: 50 }}
                  InputProps={{ classes: { input: classes.inputWhite } }}
                />
              </Grid>
              <Grid item xs={12}>
                <div>
                  <Checkbox
                    checked={hipaaAcknowledged}
                    onChange={(e) => setHipaaAcknowledged(e.target.checked)}
                    color="primary"
                    name="hipaaAcknowledged"
                  />
                  <span className={classes.infoText}>
                    I have read and acknowledge the{" "}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenHipaaModal(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.stopPropagation();
                          setOpenHipaaModal(true);
                        }
                      }}
                      style={{ textDecoration: "underline", cursor: "pointer" }}
                    >
                      Baylor Genetics HIPAA Notice of Privacy Practices
                    </span>
                  </span>
                </div>
              </Grid>
              <Grid item xs={12} className={classes.buttonBox}>
                <Button
                  type="submit"
                  variant="contained"
                  className={classes.submitButton}
                  disabled={loading || !isFormValid()}
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  {loading ? "Submitting..." : "Submit"}
                </Button>
              </Grid>
            </Grid>
          </form>
          <HipaaNoticeDialog
            openDialog={openHipaaModal}
            onClose={() => setOpenHipaaModal(false)}
          />
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AppointmentForm;
